import React, { createContext, useState, useContext, useCallback } from 'react';
import * as dataCacheService from '../services/dataCacheService';

const DataCacheContext = createContext(null);

export const useDataCache = () => useContext(DataCacheContext);

export const DataCacheProvider = ({ children }) => {
    const [cachedReviews, setCachedReviews] = useState({}); // { [cacheKey]: Review[] }
    const [loadingReviews, setLoadingReviews] = useState({}); // { [cacheKey]: boolean }
    const [cachedUsernames, setCachedUsernames] = useState({}); // { [userId]: string }
    const [loadingUsernames, setLoadingUsernames] = useState({}); // { [userId]: boolean }
    const [cachedFavorites, setCachedFavorites] = useState([]); // Added
    const [loadingFavorites, setLoadingFavorites] = useState(false); // Added

    const loadReviews = useCallback(async (cityName, locationId, token) => {
        const cacheKey = `${cityName}_${locationId}`;
        setLoadingReviews(prev => ({ ...prev, [cacheKey]: true }));
        try {
            const reviews = await dataCacheService.getReviewsWithUsernames(cityName, locationId, token);
            setCachedReviews(prev => ({ ...prev, [cacheKey]: reviews }));
        } catch (error) {
            console.error(`DataCacheContext: Error loading reviews for ${cacheKey}:`, error);
            setCachedReviews(prev => ({ ...prev, [cacheKey]: [] })); // Set empty on error
        }
        setLoadingReviews(prev => ({ ...prev, [cacheKey]: false }));
    }, []);

    const refreshReviews = useCallback(async (cityName, locationId, token) => {
        dataCacheService.invalidateLocationReviewsCache(cityName, locationId);
        await loadReviews(cityName, locationId, token);
    }, [loadReviews]);

    // Function to load a single username, primarily for direct use if needed (e.g., chat)
    const loadUsername = useCallback(async (userId) => { // Token no longer needed here
        if (!userId || cachedUsernames[userId]) return; // Don't refetch if already cached in context
        
        setLoadingUsernames(prev => ({ ...prev, [userId]: true }));
        try {
            const { displayUsername } = await dataCacheService.getDisplayUsername(userId);
            setCachedUsernames(prev => ({ ...prev, [userId]: displayUsername }));
        } catch (error) {
            console.error(`DataCacheContext: Error loading username for ${userId}:`, error);
        }
        setLoadingUsernames(prev => ({ ...prev, [userId]: false }));
    }, [cachedUsernames]);

    // --- Favorites Context Functions ---
    const loadUserFavorites = useCallback(async (token) => {
        if (!token) {
            setCachedFavorites([]);
            return;
        }
        setLoadingFavorites(true);
        try {
            const favorites = await dataCacheService.getCachedUserFavorites(token);
            setCachedFavorites(favorites);
        } catch (error) {
            console.error(`DataCacheContext: Error loading user favorites:`, error);
            setCachedFavorites([]); // Set empty on error
        }
        setLoadingFavorites(false);
    }, []);

    const addContextFavorite = useCallback(async (token, cityName, locationId, locationName) => {
        if (!token) return { success: false, error: 'Token required' }; 
        try {
            const result = await dataCacheService.addFavoriteToCache(token, cityName, locationId, locationName);
            if (result.success) {
                await loadUserFavorites(token); // Refresh favorites list
            }
            return result;
        } catch (error) {
            console.error(`DataCacheContext: Error adding favorite for ${locationId}:`, error);
            return { success: false, error: error.message || 'Failed to add favorite' };
        }
    }, [loadUserFavorites]);

    const removeContextFavorite = useCallback(async (token, cityName, locationId) => {
        if (!token) return { success: false, error: 'Token required' };
        try {
            const result = await dataCacheService.removeFavoriteFromCache(token, cityName, locationId);
            if (result.success) {
                await loadUserFavorites(token); // Refresh favorites list
            }
            return result;
        } catch (error) {
            console.error(`DataCacheContext: Error removing favorite for ${locationId}:`, error);
            return { success: false, error: error.message || 'Failed to remove favorite' };
        }
    }, [loadUserFavorites]);

    const value = {
        cachedReviews,
        loadingReviews,
        cachedUsernames,
        loadingUsernames,
        loadReviews,
        refreshReviews,
        loadUsername,
        cachedFavorites,
        loadingFavorites,
        loadUserFavorites,
        addContextFavorite,
        removeContextFavorite,
    };

    return <DataCacheContext.Provider value={value}>{children}</DataCacheContext.Provider>;
}; 